import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const fixTextHierarchySkill: Skill = {
    name: SKILL_NAMES.fixTextHierarchy,
    description:
        'Fixing a scene whose big and small text slots hold copy that is not a headline and its ' +
        'supporting line. Load when a tool finding reports a text hierarchy problem.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Fixing Text Hierarchy

Many designs pair a large text slot with a small one. That size gap is a claim: the big
line is the point, the small line supports it.

Two mistaken uses are awkward and not good:
- **Two coordinate points.** The lines make separate, equally-weighted claims — two
  benefits, two credentials, two steps, two halves of a parallel construction. Neither
  is subordinate, so ranking one above the other is arbitrary.
- **A split sentence.** One sentence runs from the big slot into the small one, so half
  of a single thought has been demoted to secondary size.

## First, decide which one you have
Read the two lines together and ask whether the smaller one is genuinely SUBORDINATE:
does it elaborate, qualify, attribute, or label the larger? If it does, the scene is
fine and needs no change. If the two could swap places without either reading oddly,
they are coordinate. If they only make sense read as one continuous sentence, it's a
split sentence.

The distinction matters because it picks the fix, and the cheapest edit only works for
one of them.

## The trap: resizing
**Swapping or equalizing the font sizes does NOT fix coordinate points.** It moves which
point got demoted, or flattens the design's intended emphasis into mush — the copy is
still two things in a slot built for one thing and its support. Resizing is the right
fix in exactly one case: the lines ARE a headline and its support, but they landed in
the wrong slots, so the supporting line is set larger than the point it supports. That
is inverted priority, and swapping the sizes is the whole fix.

If you reach for a resize on anything else, the next review will report the same defect
and you will have spent a round for nothing.

## Fixes for a split sentence
- **Merge into one slot.** Usually right. Put the whole sentence in the larger asset and
  clear the smaller one with \`${TOOL_NAMES.setText}\` (set \`text\` to \`""\`). The sentence was
  never two things.
- **Split at a real boundary.** If the merged sentence is too long for one slot, break it
  into two scenes — or two subscenes with \`${TOOL_NAMES.addSubscene}\` — at a clause boundary,
  so each holds a complete thought.

One arrangement that is NOT a defect and must be left alone: a short smaller line placed
ABOVE a short larger one, reading into it as a deliberate lead-in — a kicker setting up
a payoff. The size gap is the emphasis there, and it is working.

## Fixes for two coordinate points
Pick by what the content actually is:
- **Clear the second slot.** The fastest fix, and often correct — the second line is
  frequently filler written to avoid an empty box. A secondary asset with nothing
  genuinely subordinate to put in it should stay empty.
- **Give each point its own scene.** Right when both points are worth keeping and each
  deserves its own beat. Add a scene and move one point to it.
- **Switch to a design built for two.** Right when the points belong together — a
  comparison, a pair of credentials, a two-item list. Pick a design
  whose text assets carry equal weight, rather than degrading the copy to fit a
  title/subtitle design.
- **Rewrite the second line into real support.** The only fix that keeps both ideas in
  one scene: turn the coordinate point into a line that genuinely elaborates or qualifies
  the first. Only do this when the words are yours to change — if the user supplied the
  copy, move it rather than rewriting it.

## Before you finish
Re-read the two lines as the viewer will. The big line should carry the scene's point on
its own, and the small line should be something you could delete without losing a
separate idea. If deleting the small line would lose a point the scene needs, it was
never a subtitle — it needed its own scene or a different design.`;
    },
};
